import moment, { Moment } from 'moment';
import { alpha } from '@mui/material/styles';
import colors from '../../common/colors';
import type { DateRange, QuickFilter } from './types';

export const getCurrentMonthYear = (
  calendarContainer: HTMLElement
): { month: number; year: number } | null => {
  const monthYearElement = calendarContainer.querySelector(
    '.MuiPickersCalendarHeader-label'
  );
  if (!monthYearElement) return null;

  const monthYearText = monthYearElement.textContent;
  if (!monthYearText) return null;

  const parts = monthYearText.trim().split(' ');
  if (parts.length !== 2) return null;

  const monthName = parts[0];
  const year = parseInt(parts[1], 10);

  const testDate = moment(`${monthName} ${year}`, 'MMMM YYYY');
  if (!testDate.isValid()) return null;

  return { month: testDate.month(), year: testDate.year() };
};

export const applyRangeStyles = (
  dateRange: DateRange,
  hoveredDate: Moment | null,
  isMobile: boolean
) => {
  // Clear all existing styles
  document.querySelectorAll('.MuiPickersDay-root').forEach((day) => {
    const dayElement = day as HTMLElement;
    dayElement.style.backgroundColor = '';
    dayElement.style.color = '';
    dayElement.style.borderRadius = '';
  });

  const calendarContainers = document.querySelectorAll('[data-calendar-type]');

  calendarContainers.forEach((container) => {
    const calendarType = container.getAttribute('data-calendar-type');
    const daysInCalendar = container.querySelectorAll('.MuiPickersDay-root');

    const currentMonthYear = getCurrentMonthYear(container as HTMLElement);
    if (!currentMonthYear) return;

    daysInCalendar.forEach((day) => {
      const dayElement = day as HTMLElement;
      const dayText = dayElement.textContent;
      if (!dayText) return;

      const dayNumber = parseInt(dayText, 10);
      if (isNaN(dayNumber)) return;

      const currentDate = moment()
        .year(currentMonthYear.year)
        .month(currentMonthYear.month)
        .date(dayNumber);

      if (calendarType === 'start') {
        if (dateRange[0] && currentDate.isSame(dateRange[0], 'day')) {
          dayElement.style.backgroundColor = colors.grey_300;
          dayElement.style.color = colors.white;
          dayElement.style.borderRadius = '50%';
        } else if (
          dateRange[0] &&
          dateRange[1] &&
          currentDate.isAfter(dateRange[0], 'day') &&
          currentDate.isBefore(dateRange[1], 'day')
        ) {
          dayElement.style.backgroundColor = alpha(colors.grey_300, 0.08);
          dayElement.style.color = colors.grey_300;
          dayElement.style.borderRadius = '0';
        }

        if (!isMobile && dateRange[0] && !dateRange[1] && hoveredDate) {
          if (
            currentDate.isAfter(dateRange[0], 'day') &&
            currentDate.isBefore(hoveredDate, 'day') &&
            hoveredDate.isAfter(dateRange[0], 'day')
          ) {
            dayElement.style.backgroundColor = alpha(colors.grey_300, 0.08);
            dayElement.style.color = colors.grey_300;
            dayElement.style.borderRadius = '0';
          }
        }
      } else if (calendarType === 'end') {
        if (dateRange[1] && currentDate.isSame(dateRange[1], 'day')) {
          dayElement.style.backgroundColor = colors.grey_300;
          dayElement.style.color = colors.white;
          dayElement.style.borderRadius = '50%';
        } else if (
          dateRange[0] &&
          dateRange[1] &&
          currentDate.isAfter(dateRange[0], 'day') &&
          currentDate.isBefore(dateRange[1], 'day')
        ) {
          dayElement.style.backgroundColor = alpha(colors.grey_300, 0.08);
          dayElement.style.color = colors.grey_300;
          dayElement.style.borderRadius = '0';
        }

        if (!isMobile && dateRange[0] && !dateRange[1] && hoveredDate) {
          if (
            currentDate.isSame(hoveredDate, 'day') &&
            hoveredDate.isAfter(dateRange[0], 'day')
          ) {
            dayElement.style.backgroundColor = alpha(colors.grey_300, 0.08);
            dayElement.style.color = colors.grey_300;
            dayElement.style.borderRadius = '50%';
          } else if (
            currentDate.isAfter(dateRange[0], 'day') &&
            currentDate.isBefore(hoveredDate, 'day') &&
            hoveredDate.isAfter(dateRange[0], 'day')
          ) {
            dayElement.style.backgroundColor = alpha(colors.grey_300, 0.08);
            dayElement.style.color = colors.grey_300;
            dayElement.style.borderRadius = '0';
          }
        }
      }
    });
  });
};

// Shows partial range (e.g. "15 May 2026 - ") so the user knows what they selected
// even before the second date is picked. Returns '' only when both dates are absent.
export const formatDateRange = (
  startDate: Moment | null,
  endDate: Moment | null,
  dateFormat: string = 'DD MMM YYYY'
): string => {
  if (!startDate && !endDate) return '';
  const start = startDate?.format(dateFormat) ?? '';
  const end = endDate?.format(dateFormat) ?? '';
  return `${start} - ${end}`;
};

// Quick filter utilities
export const getThisWeek = () => {
  const start = moment().startOf('week');
  const end = moment().endOf('week');
  return [start, end] as [moment.Moment, moment.Moment];
};

export const getThisMonth = () => {
  const start = moment().startOf('month');
  const end = moment().endOf('month');
  return [start, end] as [moment.Moment, moment.Moment];
};

export const getThisYear = () => {
  const start = moment().startOf('year');
  const end = moment().endOf('year');
  return [start, end] as [moment.Moment, moment.Moment];
};

export const getDefaultFilters = (): QuickFilter[] => [
  {
    key: 'week',
    label: 'This Week',
    getDateRange: getThisWeek,
  },
  {
    key: 'month',
    label: 'This Month',
    getDateRange: getThisMonth,
  },
  {
    key: 'year',
    label: 'This Year',
    getDateRange: getThisYear,
  },
];

export const toIsoOrNull = (
  value: Moment | null,
  bound: 'start' | 'end'
): string | null => {
  if (!value) return null;
  return bound === 'start'
    ? value.startOf('day').toISOString()
    : value.endOf('day').toISOString();
};
